import { divide as numberDivide } from '@zoom/billing-common';
import Cookies from 'js-cookie';
import { translation } from '@/plugins/i18n';
import { getCurrency, getCurrencySymbol, getEffectiveCurrency, isCurrencySwitch } from '@/utils/currency';
import { NO_NEED_CHANGE_PRICE_CYCLE } from '@/common/constants';
import { billingCycleToValueMap } from '@/utils/index';

export const separatorConfig = {
  'en-US': {decimal: '.', commaStyle: ',', place: 'begin', space: false},
  'en-GB': {decimal: '.', commaStyle: ',', place: 'begin', space: false},
  'zh-CN': {decimal: '.', commaStyle: ',', place: 'end', space: true, specialSymbol: true},
  'es-ES': {decimal: ',', commaStyle: '.', place: 'end', space: true},
  'fr-FR': {decimal: ',', commaStyle: ' ', place: 'end', space: true},
  'jp-JP': {decimal: '.', commaStyle: ',', place: 'begin', space: false},
  'de-DE': {decimal: ',', commaStyle: '.', place: 'end', space: true},
  'pt-PT': {decimal: ',', commaStyle: '.', place: 'begin', space: true},
  'ru-RU': {decimal: ',', commaStyle: ' ', place: 'end', space: true, specialSymbol: true},
  'zh-TW': {decimal: '.', commaStyle: ',', place: 'end', space: true, specialSymbol: true},
  'ko-KO': {decimal: '.', commaStyle: ',', place: 'begin', space: false},
  'it-IT': {decimal: ',', commaStyle: '.', place: 'begin', space: true},
  'vi-VN': {decimal: ',', commaStyle: '.', place: 'begin', space: true},
};

/**
 *
 * @param {String: en-US | en-GB | zh-CN | zh-TW | es-ES | jp-JP | de-DE | pt-PT | ru-RU | ko-KO | it-IT | vi-VN } language
 *
 */
export const getSeparatorConfig = language => {
  return separatorConfig[language] || {decimal: '.', commaStyle: ',', place: 'begin', space: false}
};

export let separators = () => {
  // delay get the separators after cookies correction logic execute in client webview[in src/plugins/i18n.js => getLocale()]
  const separatorsConfig = getSeparatorConfig((Cookies.get('_zm_lang') || 'en-US'));
  separators = () => separatorsConfig;
  return separatorsConfig
}

export const toThousands = (num, comma = separators()['commaStyle']) => {
  return (num || 0).toString().replace(/(\d)(?=(?:\d{3})+$)/g, `$1${comma}`);
};

export const NEED_REMOVE_DECIMAL_CURRENCY = ['JPY', 'INR', 'VND'];

export const NO_DECIMAL_CURRENCIES = ['VND'];

export const NEED_REMOVE_DECIMAL_CURRENCY_FOR_GOOGLE_PAY = ['KRW'];

/**
 *
 * @param amount
 * @param options {TransferMoneyOptionsBean}
 * @return {number}
 */
export const getDecimalsWithCurrency = (amount, options = {}) => {
  if (isNaN(+amount)) return 0;

  const {fixedLength = 2, needRemoveDecimal = true} = options;

  if (!needRemoveDecimal) return fixedLength;

  const currency = getEffectiveCurrency();

  return NEED_REMOVE_DECIMAL_CURRENCY.includes(currency) ? 0 : fixedLength;
};

export class PriceHandler {
  #priceInCents
  #billingCycle
  #options

  // export values
  currency = {
    symbol: '',
    place: 'begin'
  }
  integer = ''
  decimal = ''
  constructor(priceInCents, billingCycle, options) {
    this.#priceInCents = priceInCents
    this.#billingCycle = billingCycle
    this.#options = options
  }
  static of (priceInCents, billingCycle, options) {
    return new PriceHandler(priceInCents, billingCycle, options)
  }
  build() {
    return this.currencyWrapper().priceWrapper()
  }
  currencyWrapper() {
    const { customizedCurrencySymbol } = this.#options;
    const { place } = separators();
    this.currency = {
      symbol: getCurrencyFormatSymbol(customizedCurrencySymbol),
      place: place
    }
    return this
  }
  priceWrapper() {
    const priceInCents = this.#priceInCents;
    // Nan check
    if (isNaN(priceInCents)) {
      this.integer = priceInCents.toString();
      this.decimal = '';
      return this
    }
    const options = this.#options;
    // get dollar amount, cents / 100
    const amount =  numberDivide(priceInCents, 100);
    const { forceDiff= false } = options;
    let currencyFixedLength = 2;
    const isNoDecimalCurrencies = NO_DECIMAL_CURRENCIES.indexOf(getEffectiveCurrency()) !== -1;
    currencyFixedLength = isNoDecimalCurrencies ? 0 : getDecimalsWithCurrency(priceInCents, options);
    let fixedDollars = amount.toFixed(currencyFixedLength);
    if (forceDiff && parseInt(fixedDollars) === parseFloat(fixedDollars)) {
      fixedDollars = amount.toFixed(0);
    }
    const moneySplit = fixedDollars.split('.');
    const [integerPart, decimalPart = ''] = moneySplit;
    const billingCycle = this.#billingCycle;
    const { commaStyle } = separators();
    // monthly, one month, no need to divide, just return
    if (NO_NEED_CHANGE_PRICE_CYCLE.includes(billingCycle)) {
      this.integer = `${toThousands(integerPart, commaStyle)}`;
      this.decimal = decimalPart === '00' ? '': decimalPart;
      return this
    }
    const divide = billingCycleToValueMap[billingCycle] || 1
    // transfer all to monthly price
    fixedDollars = numberDivide(fixedDollars, divide).toFixed(currencyFixedLength);
    const moneySplitByMonth = fixedDollars.split('.');
    const [integerPartByMonth, decimalPartByMonth = ''] = moneySplitByMonth;
    this.integer = `${toThousands(integerPartByMonth, commaStyle)}`;
    this.decimal = decimalPartByMonth === '00' ? '' : decimalPartByMonth;
    return this
  }
}

export const priceByMonth = (priceInCents = 0, billingCycle, options = {}) => {
  return PriceHandler
    .of(priceInCents, billingCycle, options)
    .build()
}

/**
 *
 * @param cents {number}
 * @param options {TransferMoneyOptionsBean}
 * @return {string}
 */
export const transferMoney = (cents = 0, options = {}) => {
  if (isNaN(+cents)) return cents.toString();

  const amount = numberDivide(cents, 100);
  let currencyFixedLength = 2;
  const {commaStyle, decimal} = separators();
  const {customizedCurrencySymbol, forceDiff=false, skipAddSymbol = false} = options;

  const isNoDecimalCurrencies = NO_DECIMAL_CURRENCIES.indexOf(getEffectiveCurrency()) !== -1;
  currencyFixedLength = isNoDecimalCurrencies ? 0 : getDecimalsWithCurrency(cents, options);
  let fixedDollars = amount.toFixed(currencyFixedLength);

  if (forceDiff && parseInt(fixedDollars) === parseFloat(fixedDollars)) {
    fixedDollars = amount.toFixed(0);
  }

  const moneySplit = fixedDollars.split('.');
  const formatVal = `${toThousands(moneySplit[0], commaStyle)}${moneySplit[1] ? `${decimal}${moneySplit[1]}` : ''}`;
  return skipAddSymbol ? formatVal : addSymbol(formatVal, customizedCurrencySymbol);
}

export const addSymbol = (formatVal, customizedCurrencySymbol) => {
  const {place, space, specialSymbol} = separators()
  const currencySymbol = customizedCurrencySymbol || getCurrencySymbol(getCurrency())
  const currencyTrans = 'billing.currencies.' + getCurrency().toLowerCase()
  let getCurrencyTrans;
  if(customizedCurrencySymbol) {
    getCurrencyTrans =  currencySymbol
  }else if (translation(currencyTrans) == currencyTrans) {
    console.error('Cannot get translation of this currency.');
    getCurrencyTrans = currencySymbol
  } else {
    getCurrencyTrans = translation(currencyTrans)
  }
  let formatSymbol = specialSymbol ? getCurrencyTrans : currencySymbol
  if (isCurrencySwitch()) {
    formatSymbol = currencySymbol
  }
  return place === 'begin' ? `${formatSymbol}${space ? ' ' : ''}${formatVal}` : `${formatVal}${space ? ' ' : ''}${formatSymbol}`

}

const getCurrencyFormatSymbol = (customizedCurrencySymbol) => {
  const { specialSymbol } = separators()
  const currencySymbol = customizedCurrencySymbol || getCurrencySymbol(getCurrency())
  const currencyTrans = 'billing.currencies.' + getCurrency().toLowerCase()
  let getCurrencyTrans;
  if(customizedCurrencySymbol) {
    getCurrencyTrans =  currencySymbol
  }else if (translation(currencyTrans) === currencyTrans) {
    getCurrencyTrans = currencySymbol
  } else {
    getCurrencyTrans = translation(currencyTrans)
  }
  let formatSymbol = specialSymbol ? getCurrencyTrans : currencySymbol
  if (isCurrencySwitch()) {
    formatSymbol = currencySymbol
  }
  return formatSymbol
}
